import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  EMPTY,
  finalize,
  from,
  lastValueFrom,
  of,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { ConfirmationService, MessageService } from 'primeng/api';

import { AppMainService } from '../../../../app-main.service';
import { WhatsAppService } from '../../../../misc/campaign/services/whatsapp.service';
import { WhatsAppBusinessAccount } from '../models/whatsapp';

@Component({
  selector: 'app-whatsapp-setup',
  templateUrl: 'whatsapp-setup.component.html',
  providers: [WhatsAppService],
})
export class WhatsappSetupComponent implements OnInit, OnDestroy {
  @Output() goBackView: EventEmitter<void> = new EventEmitter<void>();

  cols = [
    { header: 'Name', field: 'name', isChecked: true },
    { header: 'Phone No.', field: 'phoneNumber', isChecked: true },
    { header: 'Registered', field: 'registered', isChecked: true },
    { header: 'Active', field: 'isActive', isChecked: true },
    { header: 'Default', field: 'isDefault', isChecked: true },
  ];

  whatsAppBusinessAccounts: WhatsAppBusinessAccount[] = [];
  selectedWhatsAppBusiness: WhatsAppBusinessAccount;
  displayWhatsAppPhoneRegister: boolean = false;

  registerFrom: FormGroup;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private appMainService: AppMainService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private whatsAppService: WhatsAppService
  ) {
    this.registerFrom = this.fb.group({
      messaging_product: ['whatsapp'],
      pin: [
        '',
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
    });
  }

  ngOnInit() {
    this.whatsAppService
      .getBusinessAccounts()
      .pipe(
        tap((accounts) => (this.whatsAppBusinessAccounts = accounts)),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  backDataView(): void {
    this.goBackView.emit();
  }

  connectWithFacebook() {
    const options = {
      config_id: '301065536243251',
      response_type: 'code',
      override_default_response_type: true,
      extras: {
        setup: {},
      },
    };
    return from(
      new Promise<fb.StatusResponse>((resolve) => FB.login(resolve, options))
    )
      .pipe(
        tap(({ authResponse }) => {
          this.appMainService.isShowSpinner('FacebookConnect');
        }),
        switchMap(({ authResponse }) => {
          if (!authResponse) return EMPTY;
          return this.whatsAppService.completeEmbeddedSignUp(authResponse.code);
        }),
        switchMap(() => this.whatsAppService.getBusinessAccounts()),
        tap((accounts) => (this.whatsAppBusinessAccounts = accounts)),
        catchError((error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error!',
            detail: error?.error?.message
              ? error?.error?.Message
              : 'Unable to verify business permissions!',
          });
          console.error('Embedded Signup Failed', error);
          return of(null);
        }),
        takeUntil(this.ngUnsubscribe$),
        finalize(() => this.appMainService.isHideSpinner('FacebookConnect'))
      )
      .subscribe();
  }

  registerPhone(rowData: WhatsAppBusinessAccount): void {
    this.selectedWhatsAppBusiness = rowData;
    this.displayWhatsAppPhoneRegister = true;
  }

  cancelPhoneRegister(): void {
    this.selectedWhatsAppBusiness.registered =
      !this.selectedWhatsAppBusiness.registered;
    this.displayWhatsAppPhoneRegister = false;
  }

  async registerWhatsAppBusinessPhone(): Promise<void> {
    if (this.registerFrom.invalid) return;
    this.appMainService.isShowSpinner('RegisterPhone');
    const phone = {
      phoneNoId: this.selectedWhatsAppBusiness.id,
      pin: this.registerFrom.value.pin,
    };

    try {
      await lastValueFrom(
        this.whatsAppService.registerWhatsAppBusinessPhone(phone).pipe(
          tap(() => {
            this.displayWhatsAppPhoneRegister = false;
            this.registerFrom.reset();
            this.messageService.add({
              severity: 'success',
              summary: 'Success!',
              detail: 'Successfully register business phone number!',
            });
          }),
          catchError((error) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error!',
              detail: error?.error?.message
                ? error?.error?.Message
                : 'Unable to register business phone, please try again!',
            });
            console.error('Registration Failed', error);

            return of(null);
          }),
          finalize(() => {
            this.displayWhatsAppPhoneRegister = false;
            this.registerFrom.reset();
            this.appMainService.isHideSpinner('RegisterPhone');
          })
        )
      );
    } catch (error) {}
  }

  deregisterPhone(rowData: any): void {
    this.confirmationService.confirm({
      message: `Do you want to Deregister following ${rowData.phoneNumber} number`,
      header: 'Deregister Confirmation',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.appMainService.isShowSpinner('DeregisterPhone');
        this.whatsAppService
          .deregisterWhatsAppBusinessPhone(rowData.id)
          .pipe(
            tap(() => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success!',
                detail: 'Deregister successfully!',
              });
            }),
            catchError((error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error!',
                detail: error?.error?.message
                  ? error?.error?.Message
                  : 'Unable to deregister business phone, please try again!',
              });
              return of(null);
            }),
            switchMap(() => this.whatsAppService.getBusinessAccounts()),
            tap((accounts) => (this.whatsAppBusinessAccounts = accounts)),
            takeUntil(this.ngUnsubscribe$),
            finalize(() => this.appMainService.isHideSpinner('DeregisterPhone'))
          )
          .subscribe();
      },
    });
  }

  makeActiveInactive(id: number, isActive: boolean) {
    const status = isActive ? 'Active' : 'Inactive';
    this.confirmationService.confirm({
      header: `${status} WhatsApp Account`,
      message: `Would you like to ${status} this account?`,
      icon: 'pi pi-info-circle',
      accept: () => {
        this.whatsAppService
          .updateWhatsAppBusinessStatus(id, { isActive, isDefault: false })
          .pipe(
            switchMap(() => this.whatsAppService.getBusinessAccounts()),
            tap((accounts) => (this.whatsAppBusinessAccounts = accounts)),
            takeUntil(this.ngUnsubscribe$)
          )
          .subscribe();
      },
    });
  }

  makeDefault(rowData: any) {
    this.confirmationService.confirm({
      header: 'Default WhatsApp Account',
      message: `Would you like to set this account as the default?`,
      icon: 'pi pi-info-circle',
      accept: () => {
        const { id, isActive, isDefault } = rowData;
        this.whatsAppService
          .updateWhatsAppBusinessStatus(id, { isActive, isDefault })
          .pipe(
            switchMap(() => this.whatsAppService.getBusinessAccounts()),
            tap((accounts) => (this.whatsAppBusinessAccounts = accounts)),
            takeUntil(this.ngUnsubscribe$)
          )
          .subscribe();
      },
    });
  }

  async syncBusinessAccount() {
    try {
      this.appMainService.isShowSpinner('syncBusinessAccount');
      await lastValueFrom(this.whatsAppService.syncBusinessAccounts());
      this.whatsAppBusinessAccounts = await lastValueFrom(this.whatsAppService.getBusinessAccounts());
      this.appMainService.isHideSpinner('syncBusinessAccount');
    } catch (error: any) {
      this.appMainService.isHideSpinner('syncBusinessAccount');
      this.messageService.add({
        severity: 'error',
        summary: 'Error!',
        detail: error?.error?.message
          ? error?.error?.Message
          : 'Unable to verify business permissions!',
      });
    }
  }
}
